import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ChevronLeft, ChevronRight, Home, Lock, Check, Users, CalendarDays, Plane, Eye,
  AlertTriangle, Hourglass, X, Building2, Palmtree, CalendarOff, Clock3, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, apiErrorMessage } from "@/lib/api";
import { UserAvatar } from "@/components/UserAvatar";

const DOW = ["Lun", "Mar", "Mié", "Jue", "Vie"];
const MONTHS = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const ME = "__me__";

function ymKey(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }
function addDaysStr(iso, n) { const [y, m, d] = iso.split("-").map(Number); const dt = new Date(y, m - 1, d); dt.setDate(dt.getDate() + n); return dt; }
function isoOf(dt) { return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`; }
function todayIso() { return isoOf(new Date()); }
function fmtEs(iso) { if (!iso) return ""; const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; }

export default function HybridCalendar() {
  const qc = useQueryClient();
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [teamDate, setTeamDate] = useState(todayIso());
  const [viewUser, setViewUser] = useState(ME);
  const [absDialog, setAbsDialog] = useState(null); // { kind, start }
  const [bulkOpen, setBulkOpen] = useState(false);
  const month = ymKey(cursor);

  const teamUsersQ = useQuery({ queryKey: ["rrhh-team-users"], queryFn: async () => (await api.get("/rrhh/team-users")).data });
  const teamUsers = useMemo(() => teamUsersQ.data?.items || [], [teamUsersQ.data]);

  const params = useMemo(() => ({ month, user_id: viewUser === ME ? undefined : viewUser }), [month, viewUser]);
  const hybridQ = useQuery({ queryKey: ["rrhh-hybrid", params], queryFn: async () => (await api.get("/rrhh/hybrid", { params })).data });
  const data = hybridQ.data;
  const weeks = useMemo(() => data?.weeks || [], [data]);
  const remoteByDate = useMemo(() => {
    const m = {};
    (data?.remote_days || []).forEach((r) => { m[r.date] = r; });
    return m;
  }, [data]);
  const absenceByDate = useMemo(() => {
    const m = {};
    (data?.absences || []).forEach((a) => {
      let cur = a.start, guard = 0;
      while (cur <= a.end && guard < 400) { m[cur] = a; cur = isoOf(addDaysStr(cur, 1)); guard++; }
    });
    return m;
  }, [data]);
  const editable = !!data?.editable;
  const serverToday = data?.today || todayIso();

  const teamQ = useQuery({ queryKey: ["rrhh-hybrid-team", teamDate], queryFn: async () => (await api.get("/rrhh/hybrid/team", { params: { date: teamDate } })).data });

  const choiceQ = useQuery({
    queryKey: ["rrhh-choice"],
    queryFn: async () => (await api.get("/rrhh/hybrid/choice-status")).data,
    refetchInterval: 30 * 60 * 1000,
  });

  useEffect(() => {
    const c = choiceQ.data;
    if (c?.needs_choice && c?.now?.weekday === 0 && c?.now?.hour >= 9) {
      toast.warning("Recuerda elegir tu segundo día de oficina (miércoles o jueves)", { id: "choice-reminder", duration: 8000 });
    }
  }, [choiceQ.data]);

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["rrhh-hybrid"] });
    qc.invalidateQueries({ queryKey: ["rrhh-hybrid-team"] });
    qc.invalidateQueries({ queryKey: ["rrhh-choice"] });
    qc.invalidateQueries({ queryKey: ["rrhh-remote"] });
    qc.invalidateQueries({ queryKey: ["rrhh-cal-month"] });
    qc.invalidateQueries({ queryKey: ["rrhh-absence"] });
  };

  const weekMut = useMutation({
    mutationFn: async ({ monday, second_day }) => (await api.put("/rrhh/hybrid/week", { monday, second_day })).data,
    onSuccess: (_, v) => { invalidateAll(); toast.success(`Día de oficina: ${v.second_day === "wed" ? "miércoles" : "jueves"}`); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const bulkMonthMut = useMutation({
    mutationFn: async (second_day) => {
      const offset = second_day === "wed" ? 2 : 3;
      const targets = weeks.filter((w) => isoOf(addDaysStr(w.monday, offset)) >= serverToday);
      await Promise.all(targets.map((w) => api.put("/rrhh/hybrid/week", { monday: w.monday, second_day })));
      return { second_day, count: targets.length };
    },
    onSuccess: (r) => {
      invalidateAll(); setBulkOpen(false);
      toast.success(r.count ? `${r.count} semana(s): ${r.second_day === "wed" ? "miércoles" : "jueves"} de oficina` : "No hay semanas futuras que marcar este mes");
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const reqMut = useMutation({
    mutationFn: async ({ date, note }) => (await api.post("/rrhh/remote", { date, note })).data,
    onSuccess: (r) => { invalidateAll(); toast.success("Solicitud de teletrabajo enviada · pendiente de aprobación"); (r.warnings || []).forEach((w) => toast.warning(w)); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const cancelMut = useMutation({
    mutationFn: async (rid) => (await api.delete(`/rrhh/remote/${rid}`)).data,
    onSuccess: () => { invalidateAll(); toast.success("Solicitud cancelada"); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const absMut = useMutation({
    mutationFn: async (p) => (await api.post("/rrhh/absence", p)).data,
    onSuccess: (_, v) => {
      invalidateAll(); setAbsDialog(null);
      const msg = v.kind === "vacaciones"
        ? "Vacaciones solicitadas · pendiente de aprobación"
        : (v.time_start && v.time_end)
          ? `Ausencia por horas (${v.time_start}–${v.time_end}) solicitada · no descuenta cupo`
          : "Ausencia solicitada · pendiente de aprobación";
      toast.success(msg);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const cancelAbsMut = useMutation({
    mutationFn: async (aid) => (await api.delete(`/rrhh/absence/${aid}`)).data,
    onSuccess: () => { invalidateAll(); toast.success("Solicitud cancelada"); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const move = (n) => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + n, 1));
  const needsChoice = !!choiceQ.data?.needs_choice && viewUser === ME;

  return (
    <div className="space-y-5" data-testid="rrhh-hybrid">
      {needsChoice && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="hybrid-choice-alert">
          <p className="flex items-center gap-2 text-sm font-medium text-amber-800">
            <AlertTriangle className="h-4 w-4" />
            Esta semana aún no has indicado tu segundo día de oficina. ¿Vas miércoles o jueves?
          </p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="border-amber-400 bg-white" onClick={() => weekMut.mutate({ monday: choiceQ.data.monday, second_day: "wed" })} data-testid="hybrid-choice-wed">Miércoles</Button>
            <Button size="sm" variant="outline" className="border-amber-400 bg-white" onClick={() => weekMut.mutate({ monday: choiceQ.data.monday, second_day: "thu" })} data-testid="hybrid-choice-thu">Jueves</Button>
          </div>
        </div>
      )}

      <div className="rounded-lg border bg-muted/30 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm"><strong>Pulsa cualquier día</strong> de tu calendario para marcar oficina, pedir teletrabajo, iniciar vacaciones o <strong>solicitar ausencia</strong> (día completo, franja horaria o teletrabajo excepcional). El <strong>martes</strong> es oficina para todo el equipo y eliges <strong>un día más</strong> (miércoles o jueves) cada semana.</p>
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-muted-foreground" />
            <Select value={viewUser} onValueChange={setViewUser}>
              <SelectTrigger className="h-8 w-[200px]" data-testid="hybrid-view-user"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ME}>Mi calendario</SelectItem>
                {teamUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {!editable && <p className="mt-2 text-[11px] text-muted-foreground">Estás viendo el calendario de <strong>{data?.viewing?.name}</strong> (solo lectura).</p>}
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-2 font-heading text-lg font-semibold capitalize"><CalendarDays className="h-5 w-5 text-primary" />{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</h3>
            <div className="flex items-center gap-2">
              {editable && (
                <Popover open={bulkOpen} onOpenChange={setBulkOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8" data-testid="hybrid-bulk-month"><Building2 className="mr-1.5 h-4 w-4" />Oficina del mes</Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-60 p-2" align="end" data-testid="hybrid-bulk-pop">
                    <p className="px-1 pb-1.5 text-[11px] text-muted-foreground">Marca tu 2º día de oficina en todas las semanas (de hoy en adelante) de {MONTHS[cursor.getMonth()]}.</p>
                    <div className="space-y-1">
                      <Button size="sm" variant="outline" className="w-full justify-start" onClick={() => bulkMonthMut.mutate("wed")} disabled={bulkMonthMut.isPending} data-testid="hybrid-bulk-wed"><Building2 className="mr-1.5 h-3.5 w-3.5" />Todos los miércoles</Button>
                      <Button size="sm" variant="outline" className="w-full justify-start" onClick={() => bulkMonthMut.mutate("thu")} disabled={bulkMonthMut.isPending} data-testid="hybrid-bulk-thu"><Building2 className="mr-1.5 h-3.5 w-3.5" />Todos los jueves</Button>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => move(-1)} data-testid="hybrid-prev"><ChevronLeft className="h-4 w-4" /></Button>
                <Button variant="outline" size="sm" className="h-8" onClick={() => setCursor(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); })}>Hoy</Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => move(1)} data-testid="hybrid-next"><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>

          {hybridQ.isLoading ? <Skeleton className="h-80 w-full" /> : (
            <Card className="overflow-hidden">
              <div className="grid grid-cols-5 border-b bg-muted/40 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {DOW.map((d) => <div key={d} className="py-2">{d}</div>)}
              </div>
              <div className="divide-y">
                {weeks.map((w) => (
                  <div key={w.monday} data-testid={`hybrid-week-${w.monday}`}>
                    <div className="grid grid-cols-5">
                      {DOW.map((_, wd) => {
                        const dt = addDaysStr(w.monday, wd);
                        const iso = isoOf(dt);
                        return <DayCell key={wd} weekday={wd} dayNum={dt.getDate()} inMonth={ymKey(dt) === month}
                          secondDay={w.second_day} monday={w.monday} iso={iso} editable={editable} today={serverToday}
                          remote={remoteByDate[iso]} absence={absenceByDate[iso]}
                          onMarkOffice={(second_day) => weekMut.mutate({ monday: w.monday, second_day })}
                          onRequestRemote={(date) => reqMut.mutate({ date, note: "" })}
                          onStartVacation={(start) => setAbsDialog({ kind: "vacaciones", start, mode: "full" })}
                          onRequestAbsence={(start) => setAbsDialog({ kind: "ausencia", start, mode: "full" })}
                          onCancelRemote={(rid) => cancelMut.mutate(rid)}
                          onCancelAbsence={(aid) => cancelAbsMut.mutate(aid)} />;
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-blue-500" /> Oficina · martes</span>
            <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-emerald-500" /> Oficina · día elegido</span>
            <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-purple-500" /> Teletrabajo aprobado</span>
            <span className="flex items-center gap-1"><span className="h-3 w-3 rounded border-2 border-dashed border-purple-400 bg-purple-50" /> Teletrabajo solicitado</span>
            <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-sky-400" /> Vacaciones</span>
            <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-orange-400" /> Ausencia</span>
          </div>
        </div>

        <Card className="h-fit">
          <CardContent className="space-y-3 p-4">
            <h3 className="flex items-center gap-2 font-semibold"><Users className="h-4 w-4 text-primary" />Quién va a la oficina</h3>
            <Input type="date" value={teamDate} onChange={(e) => setTeamDate(e.target.value)} className="h-9" data-testid="hybrid-team-date" />
            {teamQ.isLoading ? <Skeleton className="h-24 w-full" /> : (
              <div className="space-y-3" data-testid="hybrid-team-list">
                <p className="text-xs text-muted-foreground">{teamQ.data?.reason}</p>
                <div>
                  <p className="mb-1.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-blue-600">En la oficina · {(teamQ.data?.in_office || []).length}</p>
                  {(teamQ.data?.in_office || []).length === 0 ? <p className="text-xs text-muted-foreground">Nadie.</p> : (
                    <div className="flex flex-wrap gap-1.5">
                      {teamQ.data.in_office.map((p) => (
                        <span key={p.id} className="flex items-center gap-1.5 rounded-full border bg-card py-1 pl-1 pr-2.5 text-xs" data-testid={`hybrid-team-${p.id}`}>
                          <UserAvatar name={p.name} color={p.color} size="xs" /> {(p.name || "").split(" ")[0]}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {(teamQ.data?.remote_voluntary || []).length > 0 && (
                  <div>
                    <p className="mb-1.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-purple-600"><Plane className="h-3 w-3" />Teletrabajo aprobado · {teamQ.data.remote_voluntary.length}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {teamQ.data.remote_voluntary.map((p) => (
                        <span key={p.id} className="flex items-center gap-1.5 rounded-full border border-purple-200 bg-purple-50 py-1 pl-1 pr-2.5 text-xs" data-testid={`hybrid-remote-${p.id}`}>
                          <UserAvatar name={p.name} color={p.color} size="xs" /> {(p.name || "").split(" ")[0]}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {(teamQ.data?.remote_requested || []).length > 0 && (
                  <div>
                    <p className="mb-1.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-amber-600"><Hourglass className="h-3 w-3" />Teletrabajo solicitado · {teamQ.data.remote_requested.length}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {teamQ.data.remote_requested.map((p) => (
                        <span key={p.id} className="flex items-center gap-1.5 rounded-full border border-dashed border-amber-300 bg-amber-50 py-1 pl-1 pr-2.5 text-xs" data-testid={`hybrid-requested-${p.id}`}>
                          <UserAvatar name={p.name} color={p.color} size="xs" /> {(p.name || "").split(" ")[0]}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {(teamQ.data?.absences || []).length > 0 && (
                  <div>
                    <p className="mb-1.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-sky-600"><Palmtree className="h-3 w-3" />Vacaciones / ausencias · {teamQ.data.absences.length}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {teamQ.data.absences.map((p) => (
                        <span key={p.id} className={`flex items-center gap-1.5 rounded-full border py-1 pl-1 pr-2.5 text-xs ${p.kind === "vacaciones" ? "border-sky-200 bg-sky-50" : "border-orange-200 bg-orange-50"} ${p.status === "pendiente" ? "border-dashed" : ""}`} data-testid={`hybrid-absent-${p.id}`}>
                          <UserAvatar name={p.name} color={p.color} size="xs" /> {(p.name || "").split(" ")[0]}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {absDialog && (
        <AbsenceDialog dialog={absDialog} today={serverToday} saving={absMut.isPending || reqMut.isPending}
          onClose={() => setAbsDialog(null)}
          onSave={(p) => absMut.mutate(p)}
          onSaveRemoteExc={(p) => { reqMut.mutate({ ...p, exceptional: true }); setAbsDialog(null); }} />
      )}
    </div>
  );
}

function AbsenceDialog({ dialog, today, onClose, onSave, saving, onSaveRemoteExc }) {
  const isVac = dialog.kind === "vacaciones";
  // subKind: solo cuando NO es vacaciones. "ausencia" | "teletrabajo_excepcional"
  const [subKind, setSubKind] = useState(dialog.kind === "vacaciones" ? "vacaciones" : "ausencia");
  const [mode, setMode] = useState(dialog.mode || "full"); // "full" | "partial"
  const [start, setStart] = useState(dialog.start);
  const [end, setEnd] = useState(dialog.start);
  const [reason, setReason] = useState("");
  const [timeStart, setTimeStart] = useState("09:00");
  const [timeEnd, setTimeEnd] = useState("11:00");

  const isExc = subKind === "teletrabajo_excepcional";
  const showModeToggle = !isVac && !isExc;
  const isPartial = showModeToggle && mode === "partial";

  const labor = useMemo(() => {
    if (!start || !end || end < start) return 0;
    let n = 0, cur = start, guard = 0;
    while (cur <= end && guard < 400) { const d = addDaysStr(cur, 0); if (d.getDay() !== 0 && d.getDay() !== 6) n++; cur = isoOf(addDaysStr(cur, 1)); guard++; }
    return n;
  }, [start, end]);

  const validVac = isVac && start && end && end >= start;
  const validExc = isExc && start && reason.trim();
  const validPartial = isPartial && start && start === end && timeStart && timeEnd && timeEnd > timeStart && reason.trim();
  const validFull = !isVac && !isExc && !isPartial && start && end && end >= start && reason.trim();
  const valid = isVac ? validVac : isExc ? validExc : (isPartial ? validPartial : validFull);

  const submit = () => {
    if (isExc) {
      onSaveRemoteExc({ date: start, note: reason });
    } else if (isPartial) {
      onSave({ kind: "ausencia", start, end: start, reason, time_start: timeStart, time_end: timeEnd });
    } else {
      onSave({ kind: isVac ? "vacaciones" : "ausencia", start, end, reason });
    }
  };

  const title = isVac ? "Solicitar vacaciones"
    : isExc ? "Teletrabajo excepcional"
    : isPartial ? "Ausencia por horas"
    : "Solicitar ausencia";
  const icon = isVac ? <Palmtree className="h-5 w-5 text-sky-600" />
    : isExc ? <Sparkles className="h-5 w-5 text-fuchsia-600" />
    : <CalendarOff className="h-5 w-5 text-orange-600" />;
  const desc = isVac
    ? "Elige el rango de vacaciones. Lo aprueban Dirección y Administración."
    : isExc
      ? "Trabajo desde casa por un motivo puntual (técnico, cita médica, gestión). NO se descuenta de tus 10 días de teletrabajo anuales."
      : isPartial
        ? "Franja horaria concreta (ej. cita médica). NO descuenta del cupo anual de vacaciones."
        : "Indica las fechas y el motivo de la ausencia. La aprueban Dirección y Administración.";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="hybrid-abs-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{icon}{title}</DialogTitle>
          <DialogDescription>{desc}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!isVac && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Tipo de solicitud</Label>
              <div className="flex overflow-hidden rounded-lg border" data-testid="hybrid-abs-kind">
                <button className={`flex-1 px-3 py-1.5 text-xs font-medium ${!isExc ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"}`}
                  onClick={() => setSubKind("ausencia")} data-testid="hybrid-kind-ausencia">
                  <CalendarOff className="mr-1 inline h-3 w-3" />Ausencia
                </button>
                <button className={`flex-1 px-3 py-1.5 text-xs font-medium ${isExc ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"}`}
                  onClick={() => setSubKind("teletrabajo_excepcional")} data-testid="hybrid-kind-exc">
                  <Sparkles className="mr-1 inline h-3 w-3" />Teletrabajo excepcional
                </button>
              </div>
            </div>
          )}
          {showModeToggle && (
            <div className="flex overflow-hidden rounded-lg border" data-testid="hybrid-abs-mode">
              <button className={`flex-1 px-3 py-1.5 text-xs font-medium ${mode === "full" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"}`}
                onClick={() => setMode("full")} data-testid="hybrid-mode-full">Día completo</button>
              <button className={`flex-1 px-3 py-1.5 text-xs font-medium ${mode === "partial" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"}`}
                onClick={() => { setMode("partial"); setEnd(start); }} data-testid="hybrid-mode-partial">
                <Clock3 className="mr-1 inline h-3 w-3" />Franja horaria
              </button>
            </div>
          )}
          {isExc ? (
            <div className="space-y-1.5">
              <Label>Día</Label>
              <Input type="date" value={start} min={today} onChange={(e) => setStart(e.target.value)} data-testid="hybrid-abs-start" />
            </div>
          ) : isPartial ? (
            <>
              <div className="space-y-1.5">
                <Label>Día</Label>
                <Input type="date" value={start} min={today} onChange={(e) => { setStart(e.target.value); setEnd(e.target.value); }} data-testid="hybrid-abs-start" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Hora inicio</Label><Input type="time" value={timeStart} onChange={(e) => setTimeStart(e.target.value)} data-testid="hybrid-abs-time-start" /></div>
                <div className="space-y-1.5"><Label>Hora fin</Label><Input type="time" value={timeEnd} onChange={(e) => setTimeEnd(e.target.value)} data-testid="hybrid-abs-time-end" /></div>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Desde</Label>
                  <Input type="date" value={start} min={today} onChange={(e) => { setStart(e.target.value); if (e.target.value > end) setEnd(e.target.value); }} data-testid="hybrid-abs-start" />
                </div>
                <div className="space-y-1.5">
                  <Label>Hasta</Label>
                  <Input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} data-testid="hybrid-abs-end" />
                </div>
              </div>
              {labor > 0 && <p className="text-[11px] text-muted-foreground">{labor} día{labor === 1 ? "" : "s"} laborable{labor === 1 ? "" : "s"}.</p>}
            </>
          )}
          <div className="space-y-1.5">
            <Label>{isVac ? "Nota (opcional)" : (isExc ? "Motivo (para Administración) *" : "Motivo *")}</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
              placeholder={isVac ? "Comentario para Administración (opcional)"
                : isExc ? "Ej.: viene el técnico a casa"
                : isPartial ? "Ej.: cita médica, gestión personal, familiar…"
                : "Describe el motivo de la ausencia"}
              data-testid="hybrid-abs-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            className={isExc ? "bg-fuchsia-600 hover:bg-fuchsia-700" : (isVac ? "bg-sky-600 hover:bg-sky-700" : "bg-orange-600 hover:bg-orange-700")}
            onClick={submit} disabled={!valid || saving} data-testid="hybrid-abs-save">
            {saving ? "Enviando…" : "Enviar solicitud"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DayCell({ weekday, dayNum, inMonth, secondDay, monday, iso, editable, today, remote, absence,
  onMarkOffice, onRequestRemote, onStartVacation, onRequestAbsence, onCancelRemote, onCancelAbsence }) {
  const [open, setOpen] = useState(false);
  const isTue = weekday === 1;
  const isWed = weekday === 2;
  const isThu = weekday === 3;
  const chosen = (isWed && secondDay === "wed") || (isThu && secondDay === "thu");
  const isOfficeDay = isTue || chosen;
  const isPast = iso < today;
  const isFuture = iso > today;

  let cls = "border-card bg-card text-muted-foreground";
  let label = "Teletrabajo";
  let Icon = Home;
  if (absence) {
    const pend = absence.status === "pendiente";
    if (absence.kind === "vacaciones") {
      cls = pend ? "border-dashed border-sky-400 bg-sky-50 text-sky-700" : "border-sky-200 bg-sky-100 text-sky-700";
      label = pend ? "Vacaciones · solic." : "Vacaciones"; Icon = Palmtree;
    } else {
      cls = pend ? "border-dashed border-orange-400 bg-orange-50 text-orange-700" : "border-orange-200 bg-orange-100 text-orange-700";
      label = pend ? "Ausencia · solic." : "Ausencia"; Icon = CalendarOff;
    }
  } else if (remote?.status === "aprobado") { cls = "border-purple-300 bg-purple-100 text-purple-700"; label = "Teletr. aprobado"; Icon = Plane; }
  else if (remote?.status === "pendiente") { cls = "border-dashed border-purple-400 bg-purple-50 text-purple-600"; label = "Solicitado"; Icon = Hourglass; }
  else if (isTue) { cls = "border-blue-200 bg-blue-50 text-blue-700"; label = "Oficina"; Icon = Lock; }
  else if (chosen) { cls = "border-emerald-200 bg-emerald-50 text-emerald-700"; label = "Oficina"; Icon = Check; }

  // Acciones (solo mi calendario y días no pasados)
  const canMarkWed = editable && !isPast && isWed && !chosen && !absence && !remote;
  const canMarkThu = editable && !isPast && isThu && !chosen && !absence && !remote;
  const canRequestRemote = editable && isFuture && isOfficeDay && !remote && !absence;
  const canVacation = editable && !isPast && !absence && !remote;
  const canAbsence = editable && !isPast && !absence && !remote;
  const canCancelRemote = editable && remote && (remote.status === "pendiente" || isFuture);
  const canCancelAbsence = editable && absence && !isPast;
  const hasActions = canMarkWed || canMarkThu || canRequestRemote || canVacation || canAbsence || canCancelRemote || canCancelAbsence;
  const isToday = iso === today;

  const cellInner = (
    <button type="button" disabled={!hasActions}
      data-testid={`hybrid-cell-${monday}-${weekday}`}
      className={`flex min-h-[68px] w-full flex-col items-start gap-1 border p-2 text-left transition-colors ${cls} ${(!inMonth || isPast) ? "opacity-50" : ""} ${hasActions ? "cursor-pointer hover:brightness-95" : "cursor-default"} ${isToday ? "ring-2 ring-inset ring-primary/40" : ""}`}>
      <span className="text-xs font-semibold">{dayNum}</span>
      <span className="flex items-center gap-1 text-[10px] font-medium"><Icon className="h-3 w-3" />{label}</span>
    </button>
  );

  if (!hasActions) return cellInner;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{cellInner}</PopoverTrigger>
      <PopoverContent className="w-60 p-2" align="start" data-testid={`hybrid-pop-${monday}-${weekday}`}>
        <p className="px-1 pb-1.5 text-xs font-semibold">{fmtEs(iso)}</p>
        {absence && (
          <p className="px-1 pb-1.5 text-[11px] text-muted-foreground">
            {absence.kind === "vacaciones" ? "Vacaciones" : "Ausencia"} · {absence.status === "aprobado" ? "aprobada" : "pendiente de aprobación"}
            {absence.reason ? ` · ${absence.reason}` : ""}
          </p>
        )}
        {absence?.approver_message && (
          <p className="px-1 pb-1.5 text-[11px] italic text-muted-foreground" data-testid={`hybrid-abs-msg-${iso}`}>💬 {absence.approver_message}</p>
        )}
        {remote && !absence && (
          <p className="px-1 pb-1.5 text-[11px] text-muted-foreground">
            {remote.status === "aprobado" ? "Teletrabajo aprobado." : "Teletrabajo pendiente de aprobación."}
          </p>
        )}
        <div className="space-y-1">
          {canMarkWed && (
            <Button size="sm" variant="outline" className="w-full justify-start" onClick={() => { onMarkOffice("wed"); setOpen(false); }} data-testid={`hybrid-markoffice-${iso}`}>
              <Building2 className="mr-1.5 h-3.5 w-3.5" />Marcar como día de oficina
            </Button>
          )}
          {canMarkThu && (
            <Button size="sm" variant="outline" className="w-full justify-start" onClick={() => { onMarkOffice("thu"); setOpen(false); }} data-testid={`hybrid-markoffice-${iso}`}>
              <Building2 className="mr-1.5 h-3.5 w-3.5" />Marcar como día de oficina
            </Button>
          )}
          {canRequestRemote && (
            <Button size="sm" variant="outline" className="w-full justify-start text-purple-700" onClick={() => { onRequestRemote(iso); setOpen(false); }} data-testid={`hybrid-request-${iso}`}>
              <Plane className="mr-1.5 h-3.5 w-3.5" />Solicitar teletrabajo
            </Button>
          )}
          {canVacation && (
            <Button size="sm" variant="outline" className="w-full justify-start text-sky-700" onClick={() => { onStartVacation(iso); setOpen(false); }} data-testid={`hybrid-vacation-${iso}`}>
              <Palmtree className="mr-1.5 h-3.5 w-3.5" />Inicio de vacaciones
            </Button>
          )}
          {canAbsence && (
            <Button size="sm" variant="outline" className="w-full justify-start text-orange-700" onClick={() => { onRequestAbsence(iso); setOpen(false); }} data-testid={`hybrid-absence-${iso}`}>
              <CalendarOff className="mr-1.5 h-3.5 w-3.5" />Solicitar ausencia
            </Button>
          )}
          {canCancelRemote && (
            <Button size="sm" variant="outline" className="w-full justify-start text-red-600" onClick={() => { onCancelRemote(remote.id); setOpen(false); }} data-testid={`hybrid-cancel-${iso}`}>
              <X className="mr-1.5 h-3.5 w-3.5" />Cancelar teletrabajo
            </Button>
          )}
          {canCancelAbsence && (
            <Button size="sm" variant="outline" className="w-full justify-start text-red-600" onClick={() => { onCancelAbsence(absence.id); setOpen(false); }} data-testid={`hybrid-cancel-abs-${iso}`}>
              <X className="mr-1.5 h-3.5 w-3.5" />Cancelar solicitud
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
